import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from './customFetcher';
import { IHutBookEntry, IHutBookEntryInput, IHutBookRoom, hutBookEntrySchema, hutBookRoomSchema } from '../Interfaces/IHutBook';

const HUT_BOOK_QUERY_KEY = ['hutBookEntry'];
const HUT_BOOK_ROOM_QUERY_KEY = ['hutBookRoom'];

export const useGetHutBookEntries = ({ includeDeleted = false }: { includeDeleted?: boolean } = {}) => {
    const { data: hutBookEntryArray, isLoading: isLoadingHutBookEntryArray } = useQuery<IHutBookEntry[]>({
        refetchOnWindowFocus: false,
        queryKey: [...HUT_BOOK_QUERY_KEY, includeDeleted],
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: `/hut-book-entries/${includeDeleted ? '?include_deleted=true' : ''}`,
            });
            return hutBookEntrySchema.array().parse(data);
        },
        initialData: [],
    });

    return { hutBookEntryArray, isLoadingHutBookEntryArray };
};

export const useGetHutBookRooms = () => {
    const { data: hutBookRoomArray, isLoading: isLoadingHutBookRoomArray } = useQuery<IHutBookRoom[]>({
        refetchOnWindowFocus: false,
        queryKey: HUT_BOOK_ROOM_QUERY_KEY,
        queryFn: async () => {
            const data = await customFetcher<unknown[]>({
                endpoint: '/hut-book/rooms/',
            });
            return hutBookRoomSchema.array().parse(data);
        },
        initialData: [],
    });

    return { hutBookRoomArray, isLoadingHutBookRoomArray };
};

export const useCreateHutBookEntry = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: createHutBookEntry, isPending: isCreatingHutBookEntry } = useMutation<
        IHutBookEntry,
        Error,
        IHutBookEntryInput
    >({
        mutationFn: (newEntry) =>
            customFetcher<IHutBookEntry>({
                endpoint: '/hut-book-entries/',
                method: 'POST',
                body: newEntry,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: HUT_BOOK_QUERY_KEY });
        },
    });

    return { createHutBookEntry, isCreatingHutBookEntry };
};

export const useUpdateHutBookEntry = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateHutBookEntry, isPending: isUpdatingHutBookEntry } = useMutation<
        IHutBookEntry,
        Error,
        IHutBookEntryInput & { id: number }
    >({
        mutationFn: ({ id, ...payload }) =>
            customFetcher<IHutBookEntry>({
                endpoint: `/hut-book-entries/${id}/`,
                method: 'PUT',
                body: payload,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: HUT_BOOK_QUERY_KEY });
        },
    });

    return { updateHutBookEntry, isUpdatingHutBookEntry };
};

export const useDeleteHutBookEntry = () => {
    const queryClient = useQueryClient();
    const { mutateAsync: deleteHutBookEntry, isPending: isDeletingHutBookEntry } = useMutation<void, Error, number>({
        mutationFn: async (id: number) => {
            await customFetcher({
                endpoint: `/hut-book-entries/${id}/`,
                method: 'DELETE',
            });
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: HUT_BOOK_QUERY_KEY });
        },
    });

    return { deleteHutBookEntry, isDeletingHutBookEntry };
};
